import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CreditCard,
  FileText,
  HardHat,
  Home,
  Pencil,
  RefreshCw,
  Package,
  Shield,
  ShieldAlert,
  UserCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { FLAGS, useFeatureFlag } from "@/admin/hooks/use-feature-flag";
import { TierBadge } from "@/shared/components/ui/tier-badge";
import { tierFinishClass } from "@/shared/lib/tier-finish";
import { toast } from "sonner";

import { AppShell } from "@/admin/components/app-shell";
import { ConfigHelp } from "@/admin/components/config-help";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { EditableDetailCard } from "@/admin/components/shared/editable-detail-card";
import { PartnerProfilePanel } from "@/admin/components/shared/partner-profile-panel";
import { PartnerCreateInlineForm } from "@/admin/components/shared/partner-create-inline-form";
import { PartsOnUnitCard } from "@/admin/components/registrations/parts-on-unit-card";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import { CustomerActionControls } from "@/admin/components/shared/customer-action-controls";
import { SharedAddressBadge } from "@/admin/components/shared/address-matches";
import { EntityActionMenu } from "@/admin/components/shared/entity-action-menu";
import { NotesPanel } from "@/admin/components/shared/notes-panel";
import { humanise } from "@/admin/lib/display-helpers";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import {
  RecordSignalStrip,
  RecordSignal,
  SignalBadge,
} from "@/admin/components/shared/record-signal-strip";
import { useHolding, useEditHolding } from "@/admin/hooks/use-products";
import { usePartnershipDetail } from "@/admin/hooks/use-partnerships";
import type { Partnership } from "@/admin/types/organisations";
import { useEntityTypeByKey, useEntityTypes } from "@/admin/hooks/use-entities";
import { useFormatting } from "@/admin/hooks/use-formatting";
import type {
  Holding,
  HoldingWarranty,
  PendingWarrantySummary,
} from "@/admin/types/products";
import { manufacturerWarrantyRecord } from "@/admin/lib/manufacturer-warranty";
import {
  entityFileDisplayName,
  entityFileDisplayUrl,
  isEntityFileObject,
  registrationCardDisplayColumns,
  purchaseCardDisplayColumns,
} from "@/admin/lib/entity-card-display";
import {
  buildGroupedHoldingCards,
  buildRegistrationConfiguredFieldItems,
  groupedHoldingCodeNames,
} from "@/admin/lib/registration-configured-fields";
import {
  formatDurationFromMonths,
  formatRegistrationReference,
} from "@/admin/lib/formatting";
import { WorkflowIssueResolveModal } from "@/admin/components/workflow-issues/workflow-issue-resolve-modal";
import { useModal } from "@/shared/components/modals/modal-provider";
import { EntityHistoryCard } from "@/shared/components/workflows";
import { Button } from "@/shared/components/ui/button";
import {
  defineField,
  formatPhoneDisplay,
  type FieldType,
} from "@/shared/lib/forms";
import { toastApiError } from "@/shared/lib/api-error";
import { computeWarrantyExpiresAt } from "@/shared/lib/warranty";

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: "Standard",
  RETAILER_WARRANTY: "Retailer",
  EXTENDED_WARRANTY: "Extended",
};

type WarrantyRecordSource = HoldingWarranty | PendingWarrantySummary;

function renderFileValue(value: Record<string, unknown>, key?: string) {
  const label = entityFileDisplayName(value) || "Uploaded file";
  const url = entityFileDisplayUrl(value);

  if (!url) return String(label);

  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-[var(--green)] underline decoration-[var(--rule)] underline-offset-4 hover:text-[var(--green-hover)]"
    >
      {String(label)}
    </a>
  );
}

function capturedFieldValue(value: unknown): ReactNode {
  if (isEntityFileObject(value)) {
    return renderFileValue(value);
  }
  if (Array.isArray(value) && value.some((item) => isEntityFileObject(item))) {
    const values = value
      .map((item, index) =>
        isEntityFileObject(item)
          ? renderFileValue(item, `file-${index}`)
          : humanise(item),
      )
      .filter(Boolean);
    return values.length > 0 ? values : "";
  }
  return humanise(value);
}

function isIssuedWarranty(
  warranty: WarrantyRecordSource,
): warranty is HoldingWarranty {
  return "uuid" in warranty;
}

function findWarranty(
  holding: Holding | undefined,
  warrantyUuid: string | undefined,
): WarrantyRecordSource | null {
  if (!holding || !warrantyUuid) return null;
  const firstIssued = holding.warranties?.[0] ?? null;
  if (warrantyUuid === `product-${holding.uuid}`) {
    return (
      firstIssued ??
      holding.pending_warranty_summary ??
      // No issued/pending warranty: surface the manufacturer-declared
      // coverage carried on the product record before falling back to
      // the bare placeholder.
      manufacturerWarrantyRecord(holding) ?? {
        option_display_name: "No warranty issued",
        class_name: holding.product_category || "Warranty",
        warranty_type: "PENDING_WARRANTY",
        coverage_scope: null,
        duration_in_months: null,
        is_lifetime: false,
        active_from: holding.created_at,
        status: holding.status,
      }
    );
  }
  return (
    holding.warranties?.find((warranty) => warranty.uuid === warrantyUuid) ??
    (warrantyUuid === "pending-warranty"
      ? (holding.pending_warranty_summary ?? firstIssued)
      : null)
  );
}

/**
 * When the router renders this page at ``/registrations/:uuid`` (vehicle
 * detail view), ``warrantyUuid`` is absent. Mirror ``buildWarrantyRows`` so
 * we resolve the same primary record as the property-style list would use.
 */
function defaultWarrantyUuidForHolding(holding: Holding): string {
  const firstIssued = holding.warranties?.[0]?.uuid;
  if (firstIssued) return firstIssued;
  if (holding.pending_warranty_summary) return "pending-warranty";
  return `product-${holding.uuid}`;
}

function buildAddress(holding: Holding): { title: string; city: string } {
  const host = holding.host;
  const addr = host?.address?.value;
  const title = addr?.address_line_1 || host?.name || "Property";
  const city = [
    addr?.locality,
    addr?.region,
    addr?.postal_code,
    addr?.country_code,
  ]
    .filter(Boolean)
    .join(", ");
  return { title, city };
}

function durationLabel(warranty: WarrantyRecordSource): string {
  if (warranty.is_lifetime) return "Lifetime";
  return formatDurationFromMonths(warranty.duration_in_months) || "-";
}

function deriveRecordStatus(
  holdingStatus: string | undefined,
  warrantyStatus: string | undefined,
): string {
  if (holdingStatus === "cancelled") return "Cancelled";
  if (holdingStatus === "transferred") return "Transferred";
  return humanise(warrantyStatus) || humanise(holdingStatus) || "Active";
}

export function WarrantyRecordPage() {
  const { uuid, warrantyUuid } = useParams<{
    uuid: string;
    warrantyUuid: string;
  }>();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { formatDate, formatCurrency, locale, currency } = useFormatting();
  const { data: holding, isLoading, isError } = useHolding(uuid ?? null);
  // Tier identity is a per-tenant capability. Off (the default) keeps the
  // plain text badge every tenant has today.
  const tiersEnabled = useFeatureFlag(FLAGS.TIERS);
  // Full profile for the partner this registration was registered against.
  // The holding payload carries only the partner's name; the attribute values
  // the profile card renders come from the partnership detail endpoint.
  const [editingPartner, setEditingPartner] = useState<Partnership | null>(
    null,
  );
  const { data: assignedPartner } = usePartnershipDetail(
    holding?.partnership?.uuid ?? null,
  );
  const { data: hostEntity } = useEntityTypeByKey("host");
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const { data: warrantyEntity } = useEntityTypeByKey("warranty");
  const { data: consumerEntity } = useEntityTypeByKey("consumer");
  const { data: entityTypes = [] } = useEntityTypes();
  const [triggerHoldingAction, setTriggerHoldingAction] = useState<
    string | null
  >(null);
  const editHolding = useEditHolding();

  const effectiveWarrantyUuid = useMemo(() => {
    if (warrantyUuid) return warrantyUuid;
    if (!holding) return undefined;
    return defaultWarrantyUuidForHolding(holding);
  }, [holding, warrantyUuid]);

  const warranty = useMemo(
    () => findWarranty(holding, effectiveWarrantyUuid),
    [holding, effectiveWarrantyUuid],
  );

  const additionalColumns = registrationCardDisplayColumns(holdingEntity);
  // Attributes tagged with a `registration_group` render in their own grouped
  // card (see `groupedHoldingCards` below); keep them out of Additional Details
  // so they are not shown twice.
  const groupedCodeNames = useMemo(
    () => groupedHoldingCodeNames(holdingEntity),
    [holdingEntity],
  );
  // Flattened rows for the installed-component captures, shown inside the
  // Product card (not a separate card) so the components read as attributes of
  // the product they were registered against.
  const installedComponentRows = useMemo(
    () =>
      buildGroupedHoldingCards({ holding, holdingEntity }).flatMap(
        (card) => card.items,
      ),
    [holding, holdingEntity],
  );
  const additionalDetailItems: CardFieldItem[] = useMemo(() => {
    if (!holding) return [];
    const rows = buildRegistrationConfiguredFieldItems({
      holding,
      columns: additionalColumns,
      entityTypes,
      locale,
      currency,
    });
    if (rows.length > 0) {
      return rows
        .filter((row) => {
          const col = additionalColumns.find((c) => c.id === row.key);
          if (col && groupedCodeNames.has(col.attribute_code_name))
            return false;
          // Relationship columns (Product / Brand / Category / SKU off the
          // product) belong to the Product card, not Additional Details. They
          // share the `holding.registration_card` config that now drives the
          // config-driven Product card elsewhere, so exclude them here to keep
          // this page's Additional Details unchanged.
          if (col?.relationship_path?.length || col?.relationship_key)
            return false;
          return col?.attribute_code_name !== "installer_name";
        })
        .map((row) => {
          const column = additionalColumns.find((c) => c.id === row.key);
          const attribute = column
            ? entityTypes
                .find((e) => e.key === column.entity_key)
                ?.attributes.find(
                  (a) => a.code_name === column.attribute_code_name,
                )
            : undefined;
          const hasRelationship = Boolean(
            column?.relationship_path?.length || column?.relationship_key,
          );
          const isComputed = attribute?.source === "COMPUTED";
          const isEditable =
            !hasRelationship && !isComputed && column?.entity_key === "holding";
          const colOpts = attribute?.configuration_data?.options as
            Array<{ label: string; value: string }> | undefined;
          return {
            field: defineField({
              code_name: isEditable ? column!.attribute_code_name : row.key,
              display_name: row.label,
              field_type: (attribute?.field_type ?? "TEXT") as FieldType,
              configuration_data: attribute?.configuration_data,
              options: colOpts,
            }),
            value: row.value,
            displayValue: row.displayValue,
            readOnly: !isEditable,
          };
        });
    }
    const holdingEntityDef = entityTypes.find((e) => e.key === "holding");
    return (holding.attribute_values ?? [])
      .filter(
        (attr) =>
          attr.code_name !== "installer_name" &&
          !groupedCodeNames.has(attr.code_name),
      )
      .map((attr) => {
        const attrDef = holdingEntityDef?.attributes.find(
          (a) => a.code_name === attr.code_name,
        );
        const opts = attrDef?.configuration_data?.options as
          Array<{ label: string; value: string }> | undefined;
        return {
          field: defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type as FieldType,
            configuration_data: attrDef?.configuration_data,
            options: opts,
          }),
          value: attr.value,
          displayValue: capturedFieldValue(attr.value),
        };
      });
  }, [
    holding,
    additionalColumns,
    entityTypes,
    locale,
    currency,
    groupedCodeNames,
  ]);

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Loading warranty record...
          </p>
        </main>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              navigate(uuid ? `/registrations/${uuid}` : "/registrations")
            }
            className="mb-5 h-auto gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to registrations
          </Button>
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5 py-10 text-center">
            <p className="text-[13px] font-medium text-[var(--ink)]">
              Warranty record could not be loaded.
            </p>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              Refresh the page or return to the registration and try again.
            </p>
          </div>
        </main>
      </AppShell>
    );
  }

  if (!holding || !warranty) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              navigate(uuid ? `/registrations/${uuid}` : "/registrations")
            }
            className="mb-5 h-auto gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to registrations
          </Button>
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Warranty record not found.
          </p>
        </main>
      </AppShell>
    );
  }

  const issuedWarranty = isIssuedWarranty(warranty) ? warranty : null;
  const registeredProductName =
    holding.product.cached_short_name ||
    holding.product.cached_long_name ||
    "Registered product";
  const parentProduct = holding.product.parent_product;
  const productName =
    parentProduct?.cached_short_name ||
    parentProduct?.cached_long_name ||
    registeredProductName;
  const variantName = registeredProductName;
  const warrantyType =
    WARRANTY_TYPE_LABELS[warranty.warranty_type] ||
    humanise(warranty.warranty_type) ||
    "Warranty";
  const warrantyCategoryName = warranty.class_name || warrantyType;
  const address = buildAddress(holding);
  const structureValues = holding.structure?.attribute_values ?? {};
  const structureName =
    holding.structure?.label ||
    humanise(structureValues.name) ||
    humanise(structureValues.structure_name) ||
    "Main structure";
  const ownerName =
    [holding.consumer_account.first_name, holding.consumer_account.last_name]
      .filter(Boolean)
      .join(" ") || "Customer";
  const activeFrom = warranty.active_from ?? holding.created_at;
  const expiresAt = computeWarrantyExpiresAt(warranty);
  const reference = formatRegistrationReference(holding);
  const statusWorkflowState =
    holding.workflow_states?.find(
      (ws) => ws.workflow_key === "lifecycle_status",
    ) ?? null;
  const recordStatus = deriveRecordStatus(
    holding.status,
    statusWorkflowState?.state_label || warranty.status,
  );
  const pendingIssues = holding.pending_exceptions ?? [];
  const hasWorkflowIssues = pendingIssues.length > 0;
  // WPS-180: the copy below used to assert "Standard warranty is already
  // active" for ANY registration with an open workflow issue, so an unpaid
  // Platinum roof was told it was on Standard.
  //
  // Defined by the NEGATIVE, and deliberately so. The decision this copy
  // describes is "grant the extended tier or stay as you are", so the only
  // thing that needs identifying is whether extended cover is already held.
  //
  // Matching `class_name === "standard"` only ever worked for a tenant who
  // names their base tier "Standard". Matching `=== "MANUFACTURER_WARRANTY"`
  // is no better: 12 Assured issues no manufacturer warranty at all - their
  // base tier is a RETAILER_WARRANTY - so every one of their registrations
  // would fall to the extended-cover copy. Anything that is not EXTENDED is
  // the cover a registration holds before an upgrade is granted, whatever
  // that tenant calls it and whichever type they issue it as.
  const needsAction =
    hasWorkflowIssues ||
    ["draft", "pending", "needs_action"].includes(
      (warranty.status || holding.status || "").toLowerCase(),
    );
  const purchaseAmount = holding.purchase_price
    ? formatCurrency(Number(holding.purchase_price))
    : "-";
  const handleAdditionalDetailsSave = async (
    values: Record<string, unknown>,
    reason?: string,
  ) => {
    try {
      await editHolding.mutateAsync({
        uuid: holding.uuid,
        attribute_values: values,
        edit_reason: reason ?? "",
      });
      toast.success("Additional details updated");
    } catch (err) {
      toastApiError(err as Error, "Failed to update additional details");
      throw err;
    }
  };
  const configuredCustomerRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(consumerEntity),
    entityTypes,
    locale,
    currency,
  });
  const configuredHostRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(hostEntity),
    entityTypes,
    locale,
    currency,
  });
  // Columns for the purchase / transaction card, gated by the fields the
  // registration step actually collected (see purchaseCardDisplayColumns).
  const purchaseCardColumns = purchaseCardDisplayColumns(
    holdingEntity,
    holding.collected_transaction_attribute_codes,
  );
  const configuredPurchaseRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: purchaseCardColumns,
    entityTypes,
    locale,
    currency,
  });
  // The warranty entity's registration_card columns are tenant-wide - every
  // warranty EAV attribute across every template. Filter to non-empty
  // (`!item.muted`) so a registered warranty's card shows only the attributes
  // it actually carries, not other templates' attributes as blank "-" rows
  // (a solar "Standard" warranty was showing OC's roofing attributes). Same
  // convention as the success card (step-review.tsx); tenant-agnostic.
  const configuredWarrantyRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(warrantyEntity),
    entityTypes,
    locale,
    currency,
  }).filter((item) => !item.muted);
  const installerName =
    holding.partnership?.downstream_organisation_name ||
    holding.attribute_values?.find((a) => a.code_name === "installer_name")
      ?.value ||
    null;
  return (
    <AppShell>
      <main className="min-h-full bg-[var(--bg)] pb-24">
        <div className="border-b-[0.5px] border-[var(--rule-light)]">
          <div className="page-container flex h-12 items-center gap-2 overflow-hidden text-[11.5px] tracking-[0.04em] text-[var(--ink-3)]">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(`/registrations/${holding.uuid}`)}
              className="h-auto shrink-0 gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to registrations
            </Button>
          </div>
        </div>

        <div className="page-container py-8">
          <section className="flex items-start justify-between gap-8">
            <div className="min-w-0">
              {/*<div className="flex flex-wrap items-center gap-2 mb-3 ">
                <span className="text-[10.5px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  Extended warranty review
                </span>
                {hasWorkflowIssues && (
                  <Badge
                    variant="outline"
                    className="h-auto border-[var(--gold-border)] bg-[var(--gold-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[var(--gold-ink)]"
                  >
                    Needs your judgement
                  </Badge>
                )}
                </div>*/}
              <div className="flex items-start gap-2">
                <h1 className="max-w-[760px] font-display text-[42px] font-light leading-[1.12] tracking-[-0.01em] text-[var(--ink)]">
                  {productName} · {variantName} · {reference}
                </h1>
              </div>
              {hasWorkflowIssues && (
                <p className="mt-2 max-w-[760px] text-[13px] font-light leading-[1.55] text-[var(--ink-3)]">
                  {t(
                    "registrations.coverBlockersUnresolved",
                    "This registration holds {{cover}} cover. The blockers below are unresolved.",
                    { cover: warrantyCategoryName },
                  )}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-4 font-mono text-[11.5px] text-[var(--ink-3)]">
                <span>
                  {tiersEnabled ? (
                    <TierBadge
                      name={warrantyCategoryName}
                      finish={warranty.class_finish}
                    />
                  ) : (
                    warrantyCategoryName
                  )}
                </span>
                <span>
                  · Registered {formatDate(holding.created_at) || "-"}
                </span>
                <span>
                  · Submitted by{" "}
                  {holding.partnership?.downstream_organisation_name ||
                    "Direct"}
                </span>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <ConfigHelp
                sections={["display.warranty_card"]}
                returnLabel="Warranty record"
                params={{
                  warrantyTemplateUuid:
                    issuedWarranty?.warranty_template?.uuid ?? null,
                }}
                size="icon-lg"
                className="border-[0.5px] border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
              />
            </div>
          </section>

          <RecordSignalStrip className="mt-7">
            <RecordSignal
              label="Registration #"
              value={
                <span className="font-mono text-[15px] font-medium text-[var(--ink)]">
                  {reference}
                </span>
              }
              sub="Reference"
            />
            <RecordSignal
              label="Status"
              value={<StatusBadge status={recordStatus} />}
              sub={
                holding.status === "cancelled" ||
                holding.status === "transferred"
                  ? "Registration status"
                  : undefined
              }
            />
            <RecordSignal
              label="Class"
              value={
                tiersEnabled ? (
                  <TierBadge
                    name={warrantyCategoryName}
                    finish={warranty.class_finish}
                    size="badge"
                  />
                ) : (
                  <SignalBadge tone="neutral">
                    {warrantyCategoryName}
                  </SignalBadge>
                )
              }
              sub={
                warrantyType.toLowerCase() ===
                warrantyCategoryName.toLowerCase()
                  ? undefined
                  : warrantyType
              }
            />
            <RecordSignal
              label="Term"
              value={
                <SignalBadge tone="gold">{durationLabel(warranty)}</SignalBadge>
              }
              sub="Coverage"
            />
            <RecordSignal
              label="Active from"
              value={activeFrom ? formatDate(activeFrom) || activeFrom : "-"}
              sub="Start date"
            />
            <RecordSignal
              label="End date"
              value={
                warranty.is_lifetime ? (
                  <SignalBadge tone="gold">Lifetime</SignalBadge>
                ) : expiresAt ? (
                  formatDate(expiresAt) || expiresAt
                ) : (
                  "-"
                )
              }
              sub={warranty.is_lifetime ? "No expiry" : "Expiry date"}
            />
          </RecordSignalStrip>

          {needsAction && hasWorkflowIssues && (
            <section className="mt-7">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="m-0 flex items-center gap-1.5 text-[11px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  <ShieldAlert className="h-3 w-3" />
                  Rule evaluation · {pendingIssues.length} blocker
                  {pendingIssues.length === 1 ? "" : "s"}
                </h2>
              </div>
              <div className="space-y-2">
                {pendingIssues.map((issue) => (
                  <div
                    key={issue.uuid}
                    className="flex items-center justify-between gap-4 rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[12.5px] font-medium text-[var(--ink)]">
                        {issue.rule_name || issue.message}
                      </div>
                      <div className="mt-0.5 truncate text-[11.5px] text-[var(--ink-3)]">
                        {issue.message}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        openModal(WorkflowIssueResolveModal, {
                          uuid: issue.uuid,
                        })
                      }
                      className="h-7 shrink-0 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-2)] hover:bg-[var(--bg)] hover:text-[var(--green)]"
                    >
                      Apply exception
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mb-2 mt-8 border-t-[0.5px] border-[var(--rule)] pt-6">
            <div className="flex items-baseline justify-between">
              <h2 className="m-0 font-display text-[22px] font-normal tracking-[-0.02em] text-[var(--ink)]">
                Registration details
              </h2>
              <span className="text-[11px] tracking-[0.04em] text-[var(--ink-3)]">
                Edits re-run the original selection flow and are captured in the
                activity log.
              </span>
            </div>
          </div>

          <section className="mt-4 grid grid-cols-2 gap-5">
            <EditableDetailCard
              title="Customer"
              icon={<UserCircle className="h-4 w-4" />}
              items={[
                // Driven by Products > Customers > Display; fixed rows only
                // when the tenant has configured none.
                ...(configuredCustomerRows.length > 0
                  ? configuredCustomerRows.map((row) => ({
                      field: defineField({
                        code_name: row.key,
                        display_name: row.label,
                        field_type: "TEXT",
                      }),
                      value: row.value,
                      displayValue: row.displayValue,
                      readOnly: true,
                    }))
                  : [
                      {
                        field: defineField({
                          code_name: "first_name",
                          display_name: "First Name",
                          field_type: "TEXT",
                        }),
                        value: holding.consumer_account.first_name,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "last_name",
                          display_name: "Last Name",
                          field_type: "TEXT",
                        }),
                        value: holding.consumer_account.last_name,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "email",
                          display_name: "Email",
                          field_type: "EMAIL",
                        }),
                        value: holding.consumer_account.email,
                        readOnly: true,
                        link: Boolean(holding.consumer_account.email),
                        onLinkClick: holding.consumer_account.email
                          ? () => {
                              window.location.href = `mailto:${holding.consumer_account.email}`;
                            }
                          : undefined,
                      },
                      {
                        field: defineField({
                          code_name: "phone",
                          display_name: "Phone",
                          field_type: "TEXT",
                        }),
                        value: formatPhoneDisplay(
                          holding.consumer_account.phone_number,
                        ),
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "mailing",
                          display_name: "Mailing",
                          field_type: "TEXT",
                        }),
                        value: holding.mailing_address?.display,
                        readOnly: true,
                      },
                    ]),
                {
                  field: defineField({
                    code_name: "profile",
                    display_name: "Profile",
                    field_type: "TEXT",
                  }),
                  value: "View customer",
                  readOnly: true,
                  link: true,
                  onLinkClick: () =>
                    navigate(`/customers/${holding.consumer_account.uuid}`),
                },
              ]}
              trailing={
                <div className="flex items-center gap-2">
                  <SharedAddressBadge
                    consumerUuid={holding.consumer_account.uuid}
                    holdingUuid={holding.uuid}
                  />
                  <CustomerActionControls
                    consumerUuid={holding.consumer_account.uuid}
                    holdingUuid={holding.uuid}
                    triggerLabel="More customer actions"
                    editButtonTestId="warranty-record-customer-edit-btn"
                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                    onNavigate={(action) => {
                      const config = action.handler_config as Record<
                        string,
                        string
                      >;
                      if (config?.action === "copy") {
                        const fieldValues: Record<string, string> = {
                          email: holding.consumer_account.email ?? "",
                        };
                        const value = fieldValues[config.field] ?? "";
                        if (value) {
                          navigator.clipboard.writeText(value);
                          toast.success("Copied to clipboard");
                        }
                        return;
                      }
                      const url = config?.url;
                      if (url) {
                        const resolved = url.replace(
                          /\{\{(\w+)\}\}/g,
                          (_: string, token: string) => {
                            const values: Record<string, string> = {
                              uuid: holding.consumer_account.uuid,
                              email: holding.consumer_account.email ?? "",
                            };
                            return values[token] ?? "";
                          },
                        );
                        if (resolved.startsWith("mailto:"))
                          window.location.href = resolved;
                        else navigate(resolved);
                      }
                    }}
                  />
                </div>
              }
              formatOptions={{ locale, currency }}
            />

            <EditableDetailCard
              title="Product"
              icon={<Package className="h-4 w-4" />}
              items={[
                {
                  field: defineField({
                    code_name: "product",
                    display_name: "Product",
                    field_type: "TEXT",
                  }),
                  value: productName,
                  readOnly: true,
                },
                // Variant axis (e.g. colour) — only when the variant value
                // is genuinely distinct from the product name. Standalone
                // products (and any case where it would just repeat the
                // product) omit the row rather than echoing the name.
                ...(variantName && variantName !== productName
                  ? [
                      {
                        field: defineField({
                          code_name: "color",
                          display_name: "Color",
                          field_type: "TEXT" as const,
                        }),
                        value: variantName,
                        readOnly: true,
                      },
                    ]
                  : []),
                {
                  field: defineField({
                    code_name: "category",
                    display_name: "Category",
                    field_type: "TEXT",
                  }),
                  value: holding.product_category,
                  readOnly: true,
                },
                // Installed components captured at registration (HOLDING EAV
                // attributes tagged with `configuration_data.registration_group`)
                // render as minimal read-only rows alongside the product's own
                // attributes: each component's Yes/No answer, plus the identified
                // component product where one was picked (the picker row folds
                // away via `visible_when` when the answer is No). Config-driven
                // and tenant-agnostic - see `installedComponentRows` above.
                ...installedComponentRows,
                {
                  field: defineField({
                    code_name: "view",
                    display_name: "View",
                    field_type: "TEXT",
                  }),
                  value: "View product",
                  readOnly: true,
                  link: true,
                  onLinkClick: () =>
                    navigate(`/manage/products/${holding.product.uuid}`),
                },
              ]}
              trailing={
                <EntityActionMenu
                  entityTypeKey="product"
                  resourceUuid={holding.product.uuid}
                  triggerLabel={`More ${productName} actions`}
                  triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                  contentClassName="min-w-[250px]"
                />
              }
              formatOptions={{ locale, currency }}
            />

            <EditableDetailCard
              // With the tier badge beside it the title does not need to
              // restate the tier: "WARRANTY - STANDARD  [Standard]" says it
              // twice. The badge carries it; the title just names the card.
              title={
                tiersEnabled
                  ? t("registrations.warrantyCard", "Warranty")
                  : t(
                      "registrations.warrantyCardWithType",
                      "Warranty · {{type}}",
                      {
                        type: warrantyType,
                      },
                    )
              }
              titleAccessory={
                tiersEnabled ? (
                  <span className="truncate text-xs font-medium uppercase tracking-[0.14em]">
                    {warrantyCategoryName}
                  </span>
                ) : undefined
              }
              headerFinishClass={
                tiersEnabled
                  ? (tierFinishClass(warranty.class_finish) ?? undefined)
                  : undefined
              }
              icon={<Shield className="h-4 w-4" />}
              items={
                configuredWarrantyRows.length > 0
                  ? configuredWarrantyRows.map((field) => ({
                      field: defineField({
                        code_name: field.key,
                        display_name: field.label,
                        field_type: "TEXT",
                      }),
                      value: field.value,
                      // The tier column renders as its tier, for the same
                      // reason a status column renders as a status badge:
                      // it is a state, not a string.
                      displayValue:
                        tiersEnabled && field.key.endsWith(":class_name") ? (
                          <TierBadge
                            name={String(field.value ?? "")}
                            finish={warranty.class_finish}
                          />
                        ) : (
                          field.displayValue
                        ),
                      readOnly: true,
                    }))
                  : [
                      {
                        field: defineField({
                          code_name: "status",
                          display_name: "Status",
                          field_type: "TEXT",
                        }),
                        value: recordStatus,
                        displayValue: <StatusBadge status={recordStatus} />,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "tier",
                          display_name: "Tier",
                          field_type: "TEXT",
                        }),
                        value: warrantyType,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "class",
                          display_name: "Class",
                          field_type: "TEXT",
                        }),
                        value: warrantyCategoryName,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "duration",
                          display_name: "Duration",
                          field_type: "TEXT",
                        }),
                        value: durationLabel(warranty),
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "active_from",
                          display_name: "Active from",
                          field_type: "TEXT",
                        }),
                        value: activeFrom
                          ? formatDate(activeFrom) || activeFrom
                          : null,
                        readOnly: true,
                      },
                      ...(!warranty.is_lifetime
                        ? [
                            {
                              field: defineField({
                                code_name: "expires",
                                display_name: "Expires",
                                field_type: "TEXT",
                              }),
                              value: expiresAt
                                ? formatDate(expiresAt) || expiresAt
                                : null,
                              readOnly: true,
                            },
                          ]
                        : []),
                    ]
              }
              trailing={
                issuedWarranty ? (
                  <EntityActionMenu
                    entityTypeKey="warranty"
                    resourceUuid={issuedWarranty.uuid}
                    triggerLabel="More warranty actions"
                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                    contentClassName="min-w-[260px]"
                  />
                ) : (
                  <EntityActionMenu
                    entityTypeKey="holding"
                    resourceUuid={holding.uuid}
                    triggerLabel="More warranty actions"
                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                    contentClassName="min-w-[260px]"
                  />
                )
              }
              formatOptions={{ locale, currency }}
            />

            <EditableDetailCard
              title="Property"
              icon={<Home className="h-4 w-4" />}
              items={
                configuredHostRows.length > 0
                  ? configuredHostRows.map((field) => ({
                      field: defineField({
                        code_name: field.key,
                        display_name: field.label,
                        field_type: "TEXT",
                      }),
                      value: field.value,
                      displayValue: field.displayValue,
                      readOnly: true,
                    }))
                  : [
                      {
                        field: defineField({
                          code_name: "address",
                          display_name: "Address",
                          field_type: "TEXT",
                        }),
                        value: address.title,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "city",
                          display_name: "City",
                          field_type: "TEXT",
                        }),
                        value: address.city,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "property_id",
                          display_name: "Property ID",
                          field_type: "TEXT",
                        }),
                        value: holding.host?.uuid.slice(0, 8),
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "structure",
                          display_name: "Structure",
                          field_type: "TEXT",
                        }),
                        value: structureName,
                        readOnly: true,
                      },
                      ...(structureName.toLowerCase() === "other" &&
                      structureValues.other_structure_type
                        ? [
                            {
                              field: defineField({
                                code_name: "other_structure_type",
                                display_name: "Other Structure Type",
                                field_type: "TEXT",
                              }),
                              value: humanise(
                                String(structureValues.other_structure_type),
                              ),
                              readOnly: true,
                            },
                          ]
                        : []),
                    ]
              }
              trailing={
                holding.host?.uuid ? (
                  <EntityActionMenu
                    entityTypeKey="host"
                    resourceUuid={holding.host.uuid}
                    triggerLabel="More property actions"
                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                    contentClassName="min-w-[230px]"
                    onNavigate={(action) => {
                      const config = action.handler_config as Record<
                        string,
                        string
                      >;
                      if (config?.url === "#edit-contractor") {
                        setTriggerHoldingAction("edit_assigned_contractor");
                      }
                    }}
                  />
                ) : undefined
              }
              formatOptions={{ locale, currency }}
            />
          </section>

          {/* Assigned contractor / installer profile. Until now the record
            only carried the partner's name ("Submitted by ...") plus a single
            Installer row on the Purchase card, so an agent had to leave the
            record to see who they were dealing with (WPS-163). Full width
            because the profile bands and the certifications table need the
            room. Title and every row come from the tenant's own partner role
            and attribute schema, so this serves any partner role, and the
            card is absent when no partner is attached. */}
          {assignedPartner && (
            <section className="mt-5">
              <DetailCard
                title={assignedPartner.role_name}
                icon={<HardHat className="h-4 w-4" />}
                collapsible={false}
                trailing={
                  <EntityActionMenu
                    entityTypeKey="holding"
                    resourceUuid={holding.uuid}
                    triggerLabel={`More ${assignedPartner.role_name.toLowerCase()} actions`}
                    triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                    contentClassName="min-w-[250px]"
                    onNavigate={(action) => {
                      const config = action.handler_config as Record<
                        string,
                        string
                      >;
                      if (config?.url === "#edit-contractor") {
                        setTriggerHoldingAction("edit_assigned_contractor");
                      }
                    }}
                  />
                }
              >
                <PartnerProfilePanel partnership={assignedPartner} />
                {/* Edit / Change offered directly in the assigned-contractor
                  section, per the ticket, rather than only inside the overflow
                  menu. Edit shows for a hand-captured partner only: one the
                  network supplied is owned there, and the API refuses a local
                  edit the next sync would overwrite. */}
                <div className="flex flex-wrap items-center gap-3 px-4 pb-3">
                  {assignedPartner.origin === "MANUAL" && !editingPartner && (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setEditingPartner(assignedPartner)}
                      className="h-auto px-0 text-[11px] text-[var(--green)]"
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      Edit {assignedPartner.role_name.toLowerCase()}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() =>
                      setTriggerHoldingAction("edit_assigned_contractor")
                    }
                    className="h-auto px-0 text-[11px] text-[var(--green)]"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" />
                    Change {assignedPartner.role_name.toLowerCase()}
                  </Button>
                </div>
                {editingPartner && (
                  <div className="px-4 pb-3">
                    <PartnerCreateInlineForm
                      roleUuid={assignedPartner.role_uuid}
                      roleName={assignedPartner.role_name}
                      editing={editingPartner}
                      onCancel={() => setEditingPartner(null)}
                      onCreated={() => setEditingPartner(null)}
                      onSelectExisting={() => setEditingPartner(null)}
                    />
                  </div>
                )}
              </DetailCard>
            </section>
          )}

          <section className="mt-5 grid grid-cols-2 gap-5">
            <EditableDetailCard
              title="Additional Details"
              icon={<FileText className="h-4 w-4" />}
              items={additionalDetailItems}
              editConfig={{
                onSave: handleAdditionalDetailsSave,
                isPending: editHolding.isPending,
                requireReason: true,
              }}
              formatOptions={{ locale, currency }}
            />
            <EditableDetailCard
              title="Purchase"
              icon={<CreditCard className="h-4 w-4" />}
              items={[
                ...(configuredPurchaseRows.length > 0
                  ? configuredPurchaseRows.map((field) => ({
                      field: defineField({
                        code_name: field.key,
                        display_name: field.label,
                        field_type: "TEXT",
                      }),
                      value: field.value,
                      displayValue: field.displayValue,
                      readOnly: true,
                    }))
                  : [
                      {
                        field: defineField({
                          code_name: "date",
                          display_name: "Date",
                          field_type: "TEXT",
                        }),
                        value: activeFrom
                          ? formatDate(activeFrom) || activeFrom
                          : null,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "price",
                          display_name: "Price",
                          field_type: "TEXT",
                        }),
                        value: purchaseAmount,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "sales_person",
                          display_name: "Sales person",
                          field_type: "TEXT",
                        }),
                        value: holding.sales_person,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "reference",
                          display_name: "Reference",
                          field_type: "TEXT",
                        }),
                        value: reference,
                        readOnly: true,
                      },
                      {
                        field: defineField({
                          code_name: "owner",
                          display_name: "Owner",
                          field_type: "TEXT",
                        }),
                        value: ownerName,
                        readOnly: true,
                      },
                    ]),
                ...(installerName
                  ? [
                      {
                        field: defineField({
                          code_name: "installer_name",
                          display_name: "Installer / Contractor",
                          field_type: "TEXT",
                        }),
                        value: installerName,
                        readOnly: true,
                      },
                    ]
                  : []),
              ]}
              trailing={
                <EntityActionMenu
                  entityTypeKey="holding"
                  resourceUuid={holding.uuid}
                  triggerLabel="More purchase actions"
                  triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                  contentClassName="min-w-[250px]"
                  externalTriggerActionKey={triggerHoldingAction}
                  onExternalTriggerReset={() => setTriggerHoldingAction(null)}
                />
              }
              formatOptions={{ locale, currency }}
            />
            <PartsOnUnitCard links={holding.part_links ?? []} />
          </section>

          <div className="mt-5 grid grid-cols-2 gap-5">
            <section id="workflow-history" className="scroll-mt-6">
              {/* The tier / terms actions on the warranty card above run
                  against the "warranty" entity, so their audit entries are
                  written under that entity key - include it here or an
                  upgrade / downgrade leaves no visible trace (WPS-180). */}
              <EntityHistoryCard
                entityKeys={["holding", "host", "consumer", "warranty"]}
                entityIds={[
                  holding.uuid,
                  holding.host?.uuid,
                  holding.consumer_account?.uuid,
                  // Every warranty on the holding, plus the one this page is
                  // showing. That is normally one of them, but naming it
                  // explicitly means a record reached directly by uuid still
                  // resolves. Deduped here because the card joins these into a
                  // single query param without deduping.
                  ...new Set(
                    [
                      ...(holding.warranties ?? []).map((w) => w.uuid),
                      issuedWarranty?.uuid,
                    ].filter((uuid): uuid is string => Boolean(uuid)),
                  ),
                ]}
                title="Holding entity log"
              />
            </section>
            {/* Notes sit beside the entity log, on the product the note is
                about. Staff read a product record, notice something, and write
                it down without leaving - the note lands on this holding, so it
                is still here next time anyone opens this product. The property
                record's Notes tab then reads every product's notes back as one
                summary. */}
            <section id="notes" className="scroll-mt-6">
              <NotesPanel
                recordId={holding.uuid}
                recordType="registration"
                canAddInternal
                canAddCustomerVisible
                allowAttachments
              />
            </section>
          </div>
        </div>
      </main>
    </AppShell>
  );
}
